import { i18n } from "@lingui/core";
import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES } from "@ando/i18n";
// Static import of the IT catalog — DEFAULT_LOCALE is "it" and the macro
// strips the ``message`` fallback in production builds (Lingui v5 default),
// so we MUST have the real catalog already loaded by the time
// module-scope ``i18nSingleton._(msg`…`)`` calls fire from the connector
// wizard specs. Previously we activated an EMPTY catalog here and relied
// on the source string as fallback — which never landed because the
// ``message`` field was stripped, leaving only the hashed id (Y2fqqH …)
// and Lingui's "Uncompiled message detected!" console spam. The Vite
// plugin rewrites ``.po`` imports to the compiled ``.mjs`` map at build
// time so this is the right (and only) handle.
import { messages as defaultMessages } from "./locales/it/messages.po";

// SYNCHRONOUS bootstrap activation — Phase 4 (Jun 4 2026) introduced
// module-scope ``i18n._(msg`…`)`` calls inside connector wizard specs
// (crmSpec.tsx, accountingSpec.tsx, etc.) that populate
// ``CapabilityWizardSpec.title`` / ``description`` at IMPORT time, NOT
// at render. Those specs evaluate before ``main.tsx`` finishes awaiting
// ``setupI18n()``, so the singleton MUST be activated with a real catalog
// at module load — not an empty one. ``setupI18n()`` below swaps the
// active locale if the user prefers something other than IT.
i18n.load(DEFAULT_LOCALE, defaultMessages);
i18n.activate(DEFAULT_LOCALE);

/**
 * Lingui v5 runtime bootstrap.
 *
 * Catalogs live under `src/locales/{locale}/messages.po` and are compiled to JS
 * by `@lingui/vite-plugin` at build time (the plugin intercepts `.po` imports
 * and returns the compiled message map). For SPA bundles we dynamically import
 * the active locale to avoid shipping all 5 catalogs to every user.
 *
 * We re-export `SUPPORTED_LOCALES` / `DEFAULT_LOCALE` from `@ando/i18n` so the
 * source of truth stays in one package.
 */

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
export type { Locale };

const LOCALE_STORAGE_KEY = "ando:locale";

function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function detectLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isSupportedLocale(stored)) return stored;
  } catch {
    // localStorage may be unavailable (private mode, SSR); fall through to navigator.
  }

  const nav = window.navigator.language?.slice(0, 2).toLowerCase();
  if (nav && isSupportedLocale(nav)) return nav;
  return DEFAULT_LOCALE;
}

export async function loadCatalog(locale: Locale): Promise<void> {
  // Default locale (IT) is already statically loaded at module init — skip
  // the redundant dynamic import + re-activate (we'd just feed Lingui the
  // same map again). For every other locale, lazy-load the compiled .mjs
  // via the Vite plugin's ``.po``-import rewrite.
  if (locale === DEFAULT_LOCALE) {
    i18n.activate(locale);
    return;
  }
  const { messages } = await import(`./locales/${locale}/messages.po`);
  i18n.load(locale, messages);
  i18n.activate(locale);
}

export async function setupI18n(locale: Locale = detectLocale()): Promise<Locale> {
  await loadCatalog(locale);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore — locale persistence is best-effort.
  }
  return locale;
}

/**
 * Apply a locale at runtime (post-boot, e.g. after the user saves a new
 * language preference) — DRY wrapper that no-ops when the requested locale
 * is already active. Returns the activated locale (same input unless
 * unsupported, in which case it falls back to DEFAULT_LOCALE).
 *
 * Used by ``useSyncUserLocale`` (settings → BE → Lingui) so the dropdown
 * change reflects in the UI without a full reload.
 */
export async function applyLocale(locale: string): Promise<Locale> {
  const target = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;
  if (i18n.locale === target) return target;
  return setupI18n(target);
}

/** Currently-active locale (the value Lingui will render with). */
export function getActiveLocale(): Locale {
  return isSupportedLocale(i18n.locale) ? i18n.locale : DEFAULT_LOCALE;
}

export { i18n };
